import { useEffect, useState } from 'react';

import { Canvas } from '@/components/canvas/Canvas';
import { ChatPane } from '@/components/chat/ChatPane';
import { ProjectRail } from '@/components/projects/ProjectRail';
import {
  API_KEY_STORAGE_KEY,
  SettingsDrawer,
} from '@/components/settings/SettingsDrawer';
import { Button } from '@/components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { Snackbar } from '@/components/ui/snackbar';
import { useApp } from '@/store/useApp';

const TYPING_TAG_NAMES = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export default function App() {
  const init = useApp((s) => s.init);
  const projects = useApp((s) => s.projects);
  const activeProjectId = useApp((s) => s.activeProjectId);
  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;
  const undoLastEdit = useApp((s) => s.undoLastEdit);
  const artifactHtml = useApp((s) => s.artifactHtml);
  const saveCurrentDesign = useApp((s) => s.saveCurrentDesign);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSnack, setSavedSnack] = useState(false);

  useEffect(() => {
    void init();
  }, [init]);

  useEffect(() => {
    const hasKey = Boolean(window.localStorage.getItem(API_KEY_STORAGE_KEY));
    if (!hasKey) setSettingsOpen(true);
  }, []);

  // ⌘Z / Ctrl+Z — undo the last direct text edit. We intentionally bail out when
  // the user is typing into an input, textarea, contenteditable, or the iframe
  // (Canvas iframe owns its own edit semantics inside Edit mode). This way the
  // shortcut only fires for the canvas-level undo, not for every focused field.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const isUndo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey;
      if (!isUndo) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        if (TYPING_TAG_NAMES.has(target.tagName)) return;
        if (target.isContentEditable) return;
        if (target.tagName === 'IFRAME') return;
      }
      e.preventDefault();
      void undoLastEdit();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undoLastEdit]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
        <div className="flex items-baseline gap-3">
          <p className="text-sm font-semibold tracking-tight">Claude Design Clone</p>
          <p className="text-xs text-neutral-400">{activeProject?.name ?? 'No project'}</p>
        </div>
        <div className="flex items-center gap-2">
          {artifactHtml && (
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => {
                setSaving(true);
                void saveCurrentDesign().then(() => setSavedSnack(true)).finally(() => setSaving(false));
              }}
              title="Save a snapshot of the current design to Saved Files"
            >
              {saving ? 'Saving…' : 'Save Design'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(true)}>
            Settings
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <ProjectRail />
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          <ResizablePanel defaultSize={26} minSize={20} maxSize={60}>
            <ChatPane />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={74} minSize={40}>
            <Canvas />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <SettingsDrawer open={settingsOpen} onOpenChange={setSettingsOpen} />
      <Snackbar message="Design saved successfully" show={savedSnack} onHide={() => setSavedSnack(false)} />
    </div>
  );
}
